import {
    disposable, dispose, shouldDispose
} from "./dispose.ts";

import {
    getTargetId, observable, setGetHook, subscribe,
    type GetEvent
} from "./observable.ts";

import {
    debounce, evalFactor, getDebug, logMessage, type Factor
} from "./utils.ts";

/** API returned by reactTo. */
type ReactToApi<T> = {
    /** React to a value change with a callback */
    with : (
        withCallback: (value?: T) => any, 
        options? : { reset? : boolean }
    ) => ReactToApi<T>;

    /** React to a value change with a complete reinitialization. */
    withReset : (
        withCallback: (value?: T) => any
    ) => ReactToApi<T>;
}

/** React to a changing value */
export const reactTo = <T>(
    func : Factor<T>, 
    delay = 0
) => {
    const state = observable({
        computed: undefined as T
    });

    const events : GetEvent[] = [];

    const hook = (
        event : GetEvent
    ) => {
        if(!events.some(e => getTargetId(e.proxy) === getTargetId(event.proxy) && e.prop === event.prop)) {
            events.push(event);
            subscribe(event.proxy, event.prop, compute);
        }
    }

    const reactToObj = Symbol("reactTo");
    shouldDispose(() => dispose(reactToObj, func));

    let compute = () => {
        return disposable(reactToObj, () => {
            try {
                setGetHook(hook);
                state.computed = evalFactor(func);
                setGetHook();
            } finally {
                setGetHook();
            } 
        });   
    }

    compute();

    const withObj = Symbol("with");
    shouldDispose(() => dispose(withObj));

    const reactToApi = {
        with: (
            withCallback: (value?: T) => any, 
            { reset } : { reset? : boolean } = {}
        ) => {
            const wrapped = () => {
                if(reset) {
                    dispose(withObj, withCallback);
                }
                disposable(withObj, () => withCallback(state.computed));
            }
            disposable(reactToObj, () => {
                subscribe(state, "computed", debounce(({ value }) => {
                    if(getDebug()) {
                        console.log(logMessage(value, func));
                    }
                    wrapped();
                }, delay));
                wrapped(); 
            })
            return reactToApi;
        },

        withReset: (
            withCallback: (value?: T) => any
        ) => reactToApi.with(withCallback, { reset: true })

    } as ReactToApi<T>;

    return reactToApi;
}

// export const compute = <Target, Value>(
//     target : Factor<Target>, 
//     prop : KeyOf<Target, Value | undefined>) => ({
//     with: (value : () => Value) => reactTo(value).with((v) => {
//         const t = evalFactor(target) as Target;
//         if(t) {
//             (t as any)[prop] = v;
//         }
//     })
// });

/** Log changes to a value */
export const log = (
    signal : Factor<any>
) => reactTo(signal).with(v => console.log(logMessage(v, signal)));
